import type { QuaternionTuple, Vector3Tuple } from 'three';
import { create } from 'zustand';

import { TOOL_CONST } from '@/constant';
import { storage } from '@/package';
import type {
  CameraStorageInfo,
  ThreeState,
  ToolMainCameraStoreActions,
  ToolMainCameraStoreState,
} from '@/type';

import useTaskFrameStore from './taskFrameStore';

const CAMERA_VIEW_MODE_LIST = Object.values(TOOL_CONST.TOOL_CAMERA_VIEW_MODE);
const CAMERA_VIEW_MODE_LIST_LAST_INDEX = CAMERA_VIEW_MODE_LIST.length - 1;
const PRECISION_DIGITS = 3;
const DEFAULT_CAMERA_INFO: CameraStorageInfo = {
  distance: 50,
  position: [0, 0, 50],
  quaternion: [0, 0, 0, 0],
  target: [0, 0, 0],
};

const RELATED_CAMERA_MODES = {
  DEFAULT: [
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT,
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE,
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP,
  ],
  NON_DEFAULT: [
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT,
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT,
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE,
    TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP,
  ],
} as const;

const INIT_STATE = {
  modeIndex: 0,
};

const getTaskIdentifier = (mode: TOOL_CONST.TOOL_CAMERA_VIEW_MODE) => {
  const task = useTaskFrameStore.getState().selectedTask;
  if (!task) return null;

  return `PROJECT_${task.projectID}_TASK_${task.id}_${mode}_LATEST`;
};

const formatNumber = (value: number): number =>
  +value.toFixed(PRECISION_DIGITS);

const formatVector3 = (values: readonly number[]): Vector3Tuple =>
  values.map(formatNumber) as Vector3Tuple;

const formatQuaternion = (values: readonly number[]): QuaternionTuple =>
  values.map(formatNumber) as QuaternionTuple;

const getCameraMetrics = (
  camera: ThreeState['camera'],
  controls: ThreeState['controls'],
) => {
  const position = formatVector3(camera.position.toArray());
  const quaternion = formatQuaternion(camera.quaternion.toArray());
  const target = formatVector3(controls.target.toArray().toSpliced(2, 1, 0));
  const [x, y] = position;
  const distance = formatNumber(camera.position.distanceTo({ x, y, z: 0 }));

  return { position, quaternion, target, distance };
};

const saveRelatedModes = (
  modes: readonly TOOL_CONST.TOOL_CAMERA_VIEW_MODE[],
  updateData: Partial<CameraStorageInfo>,
) => {
  modes.forEach(mode => {
    const taskIdentifier = getTaskIdentifier(mode);
    if (!taskIdentifier) return;

    const existing = getSavedCameraInformation(mode);
    storage.local.set<CameraStorageInfo>(
      TOOL_CONST.KEYS.STORAGE_TOOL_CAMERA`${taskIdentifier}`,
      { ...existing, ...updateData },
    );
  });
};

const getSavedCameraInformation = (
  mode: TOOL_CONST.TOOL_CAMERA_VIEW_MODE,
): CameraStorageInfo => {
  const taskIdentifier = getTaskIdentifier(mode);
  if (!taskIdentifier) return DEFAULT_CAMERA_INFO;

  const savedCameraInfo = storage.local.get<CameraStorageInfo>(
    TOOL_CONST.KEYS.STORAGE_TOOL_CAMERA`${taskIdentifier}`,
  );

  return savedCameraInfo ?? DEFAULT_CAMERA_INFO;
};

const saveCameraInformation = (
  camera: ThreeState['camera'],
  controls: ThreeState['controls'],
  mode: TOOL_CONST.TOOL_CAMERA_VIEW_MODE,
) => {
  const taskIdentifier = getTaskIdentifier(mode);
  if (!taskIdentifier) return;

  const isDefaultMode = mode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT;
  const metrics = getCameraMetrics(camera, controls);
  const saved = getSavedCameraInformation(mode);

  // Update related modes
  if (isDefaultMode) {
    const { position, target, distance } = metrics;
    saveRelatedModes(RELATED_CAMERA_MODES.DEFAULT, {
      position,
      target,
      distance,
    });
  } else {
    const { target } = metrics;
    saveRelatedModes(RELATED_CAMERA_MODES.NON_DEFAULT, { target });
  }

  const cameraInfo: CameraStorageInfo = isDefaultMode
    ? metrics
    : { ...saved, ...metrics };

  storage.local.set<CameraStorageInfo>(
    TOOL_CONST.KEYS.STORAGE_TOOL_CAMERA`${taskIdentifier}`,
    cameraInfo,
  );
};

const useToolMainCameraStore = create<
  ToolMainCameraStoreState & ToolMainCameraStoreActions
>((set, get) => ({
  ...INIT_STATE,

  clear() {
    set({ ...INIT_STATE });
  },

  getMode() {
    return CAMERA_VIEW_MODE_LIST[get().modeIndex];
  },

  getSavedCameraInfo(mode) {
    return getSavedCameraInformation(mode);
  },

  saveCameraInfo(camera, controls, mode) {
    saveCameraInformation(camera, controls, mode);
  },

  setMode(index) {
    set({ modeIndex: index });
  },

  switchMode() {
    const { modeIndex, setMode } = get();
    const nextIndex =
      modeIndex === CAMERA_VIEW_MODE_LIST_LAST_INDEX ? 0 : modeIndex + 1;

    setMode(nextIndex);
  },
}));

export default useToolMainCameraStore;
